<script setup>
import { computed } from 'vue';
import { cva } from 'class-variance-authority';
import { arabicFontClass, useQuranSettings } from '@/composables/quran/useQuranSettings';
import { arabicWords } from '@/utils/quran/marks';

/**
 * Uthmani text, word by word. Words in `marks` are highlighted; with
 * `interactive`, every word is a button that emits `word` — the reader uses
 * it to show how often that word occurs in the Quran.
 */
const props = defineProps({
  text: { type: String, required: true },
  marks: { type: Array, default: () => [] },
  size: { type: Number, default: 3 },
  interactive: { type: Boolean, default: false },
  align: { type: String, default: 'right' },
});

const emit = defineEmits(['word']);
const settings = useQuranSettings();

const words = computed(() => arabicWords(props.text));
const marked = computed(() => new Set(props.marks));

const block = cva('text-deen-ink leading-[2.15] [word-spacing:0.08em]', {
  variants: {
    size: {
      1: 'text-[1.5rem]',
      2: 'text-[1.75rem]',
      3: 'text-[2rem] sm:text-[2.15rem]',
      4: 'text-[2.4rem] sm:text-[2.6rem]',
      5: 'text-[2.8rem] sm:text-[3.1rem]',
    },
    // justify: the mushaf's own layout — full lines, the last one centred. Only
    // where lines hold enough words; on a phone it would tear lines apart.
    align: { right: 'text-right', center: 'text-center', justify: 'text-center lg:text-justify lg:[text-align-last:center]' },
  },
});

// Each word is its own box with a tight line height: a highlight then covers
// the word, not Amiri Quran's very tall inline box that reaches into the line above.
const word = cva('inline-block leading-[1.75] px-1 -mx-0.5 rounded-lg transition-colors duration-150', {
  variants: {
    marked: { true: 'bg-deen-gold/60', false: '' },
    interactive: {
      true: 'cursor-pointer hover:bg-deen-gold/30 hover:text-deen-ink focus-visible:outline-none focus-visible:bg-deen-gold/40',
      false: '',
    },
  },
});
</script>

<template>
  <p dir="rtl" lang="ar" :class="[block({ size, align }), arabicFontClass(settings.arabicFont)]">
    <template v-for="(w, i) in words" :key="i">
      <span
        v-if="interactive"
        role="button"
        tabindex="0"
        :class="word({ marked: marked.has(i), interactive: true })"
        @click="emit('word', { word: w, index: i, el: $event.currentTarget })"
        @keydown.enter.prevent="emit('word', { word: w, index: i, el: $event.currentTarget })"
      >{{ w }}</span>
      <span v-else :class="word({ marked: marked.has(i), interactive: false })">{{ w }}</span>
      {{ ' ' }}
    </template>
    <slot />
  </p>
</template>
